import { findMenuOfferForItem } from "@/lib/menu-offer"
import type { CartItem, Category, Item, ItemOffer, OptionGroup } from "@/types"
import type { CatalogItemRef } from "@/types/loyalty-benefits"

/**
 * Why a free-product benefit needs a cart line at all.
 *
 * Open Delivery has no "free item" concept: `unitPrice` is what the customer
 * pays and the abatement rides in `discounts[]` (the spec is explicit that
 * discounts "must be reported in the `discounts` object", never folded into
 * the item price). So the reward is an ordinary line at full price plus an
 * ITEM-targeted discount equal to that line's total — which is also exactly
 * what loyalty matches on server-side.
 *
 * Before this, the benefit only resolved when the customer had *already added
 * and was paying for* the product; otherwise the claim was silently dropped at
 * submit while the card stayed ticked.
 */

/** A free line is always a single unit with no options picked. */
const FREE_PRODUCT_QUANTITY = 1

export type FreeProductResolution =
  | { ok: true; cartItem: CartItem }
  | { ok: false; reason: "offer-not-found" | "item-not-found" | "requires-options" }

function matchesRef(ref: CatalogItemRef, item: Item): boolean {
  if (ref.externalCode && item.externalCode === ref.externalCode) return true
  return !!ref.itemId && item.id === ref.itemId
}

/**
 * Whether a bag line is the product a benefit points at. Either identity
 * resolves it: the POS code (externalCode) is optional in the merchant catalog,
 * so benefits on POS-code-less items are matched by the catalog id the order
 * carries for the line (its MerchantItemId). A reference with neither matches
 * nothing.
 */
export function refMatchesCartLine(ref: CatalogItemRef, line: CartItem): boolean {
  if (ref.externalCode && line.item?.externalCode === ref.externalCode) return true
  const catalogId = line.item?.id || line.itemOffer?.itemId || ""
  return !!ref.itemId && catalogId === ref.itemId
}

/**
 * The bag line to take back when the customer drops a free-product reward.
 *
 * Only a line this checkout added on their behalf is returned: `claimedLineId`
 * is recorded at claim time, and a product the customer had already added
 * themselves never gets one — that line is theirs and must survive.
 *
 * The stored id can go stale, because cart sync swaps the browser-local line id
 * for the server's once the ADD lands. The fallback is safe: the only line this
 * checkout ever adds is a single unit of the rewarded product, no options.
 */
export function findClaimedFreeProductLine(
  items: CartItem[],
  ref: CatalogItemRef,
  claimedLineId: string | undefined,
): CartItem | undefined {
  if (!claimedLineId) return undefined
  const byId = items.find((line) => line.id === claimedLineId)
  if (byId) return byId
  return items.find(
    (line) => refMatchesCartLine(ref, line) && line.quantity === FREE_PRODUCT_QUANTITY,
  )
}

/**
 * Builds the cart line for a free-product reward, or explains why it cannot be
 * added automatically.
 *
 * An item whose offer carries option groups is refused rather than guessed at:
 * a mandatory group has no safe default, and silently adding an incomplete
 * line would produce an order the merchant rejects.
 */
export function buildFreeProductCartItem(
  ref: CatalogItemRef,
  catalog: {
    items: Item[]
    itemOffers: ItemOffer[]
    optionGroups: OptionGroup[]
    categories: Category[]
  },
  newLineId: string,
): FreeProductResolution {
  const item = catalog.items.find((candidate) => matchesRef(ref, candidate))
  if (!item) return { ok: false, reason: "item-not-found" }

  const itemOffer = findMenuOfferForItem(item.id, catalog.itemOffers, catalog.categories)
  if (!itemOffer) return { ok: false, reason: "offer-not-found" }

  const optionGroupIds = itemOffer.optionGroupsId ?? []
  const hasOptionGroups = catalog.optionGroups.some(
    (group) => optionGroupIds.includes(group.id) && group.minPermitted > 0,
  )
  if (hasOptionGroups) return { ok: false, reason: "requires-options" }

  const unitPrice = itemOffer.price.value

  return {
    ok: true,
    cartItem: {
      id: newLineId,
      item,
      itemOffer,
      itemOptions: {},
      optionGroups: [],
      optionQuantities: {},
      quantity: FREE_PRODUCT_QUANTITY,
      itemTotalPrice: unitPrice,
      unitPrice,
      specialInstructions: "",
    },
  }
}
